import React, { useState } from 'react';
import * as S from './DeleteLog.styled';
import EscapeButton from 'pages/components/EscapeButton/EscapeButton';
import { Log, useLogs } from '../hooks';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { deleteLog, selectLogsChecked, setLogsChecked } from '../redux';
import Loader from 'pages/components/Loader/Loader';
import { toast } from 'utils/toast';
import { selectCurrency } from 'pages/redux';
import { useSocketEmit } from 'pages/Navigation/hooks';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { displayActivityLogType, LOG_TYPE_DELETED } from '../activityLogLabels';
import { SHOW_ALL_ACTIVITY_LOGS, boActionLeafId } from '../activityGroupsModel';

const DeleteLog = ({
  onExit,
  log,
} : {
  onExit: Function,
  log: Log,
  /** @deprecated unused — table row index was shown as #id before UUID logs */
  id?: string | number
}) => {
  const displayLog = { ...log };
  const logsChecked = useAppSelector(selectLogsChecked);
  const currency = useAppSelector(selectCurrency);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { deleteLocalLog, updateLocalLog } = useLogs();
  const socketEmit = useSocketEmit();

  const dispatch = useAppDispatch();
  const amountLabel = `${currency}${Number(displayLog.value).toLocaleString()}`;
  const typeLabel = displayActivityLogType(displayLog.type);

  const onDeleteLog = async () => {
    const logToDelete = {
      dId: displayLog.id.toString(),
      type: displayLog.type,
      value: displayLog.value
    };
    setIsLoading(true);

    const res = await dispatch(deleteLog(logToDelete));

    if (res.status === 'success') {
      deleteLocalLog(displayLog.id);
      // Prefer the API-returned "Log deleted" row so it still appears if the
      // socket get_added_log event is lost during listener rebind.
      if (res.data && typeof res.data === 'object' && res.data.id != null) {
        updateLocalLog(res.data as Log);
      }
      socketEmit('delete_log', { logId: displayLog.id });
      // Old type-name tokens are stripped by migrate — use Show all + BO leaf id.
      const logDeletedLeaf = boActionLeafId(LOG_TYPE_DELETED);
      const nextChecked = Array.from(new Set([
        ...logsChecked.filter((c: string) => c !== 'Log deleted' && c !== LOG_TYPE_DELETED),
        SHOW_ALL_ACTIVITY_LOGS,
        logDeletedLeaf,
      ]));
      dispatch(setLogsChecked(nextChecked));
      setIsLoading(false);
      toast('Successfully deleted activity log', { type: 'success' });
      onExit(true);
      return;
    }

    setIsLoading(false);
    toast(res.data, { type: 'error' });
  };

  return (
    <>
      {
        isLoading ? <Loader isSticky /> : (
          <S.Container>
            <EscapeButton onClick={() => onExit()} />
            <S.Header>
              <S.DangerIcon aria-hidden>
                <DeleteOutline fontSize="inherit" />
              </S.DangerIcon>
              <S.MainLabel>Delete activity log</S.MainLabel>
              <S.SubLabel>
                This removes the entry from your activity log. It can be restored later.
              </S.SubLabel>
            </S.Header>
            <S.EntryCard>
              <S.EntryCardLabel>Amount</S.EntryCardLabel>
              <S.EntryAmount>{amountLabel}</S.EntryAmount>
              <S.EntryMeta>
                {typeLabel}
                {' · '}
                <S.EntryId>log</S.EntryId>
              </S.EntryMeta>
            </S.EntryCard>
            <S.Actions>
              <S.SecondaryBtn type="button" onClick={() => onExit()}>
                Cancel
              </S.SecondaryBtn>
              <S.DangerBtn type="button" onClick={() => onDeleteLog()}>
                Delete entry
              </S.DangerBtn>
            </S.Actions>
          </S.Container>
        )
      }
    </>
  );
};

export default DeleteLog;
